/**
 * Data Transformation Layer
 * 
 * Utilities to convert between Supabase database types and IndexedDB offline types.
 * Handles field mapping, type conversion, and metadata management.
 */

import type { Database } from '@/integrations/supabase/types';
import type {
  TrainingSessionOffline,
  TechniqueOffline,
  UserTechniqueOffline,
  PositionOffline,
  GamePlanOffline,
  UserProfileOffline,
  RoadmapMacroOffline,
  RoadmapMesoOffline
} from '../indexeddb/schema';
import type { Json } from '@/integrations/supabase/types';

// Supabase table row types
type TrainingSessionRow = Database['public']['Tables']['training_sessions']['Row'];
type TechniqueRow = Database['public']['Tables']['techniques']['Row'];
type UserTechniqueRow = Database['public']['Tables']['user_techniques']['Row'];
type PositionRow = Database['public']['Tables']['positions']['Row'];
type GamePlanRow = Database['public']['Tables']['game_plans']['Row'];
type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type RoadmapMacroRow = Database['public']['Tables']['roadmap_macro']['Row'];
type RoadmapMesoRow = Database['public']['Tables']['roadmap_meso']['Row'];

// Supabase insert types
type TrainingSessionInsert = Database['public']['Tables']['training_sessions']['Insert'];
type TechniqueInsert = Database['public']['Tables']['techniques']['Insert'];
type UserTechniqueInsert = Database['public']['Tables']['user_techniques']['Insert'];
type GamePlanInsert = Database['public']['Tables']['game_plans']['Insert'];
type ProfileUpdate = Database['public']['Tables']['profiles']['Update'];

/**
 * Training Session Transformations
 */
export class TrainingSessionTransformer {
  /**
   * Convert Supabase row to offline format
   */
  static fromSupabase(row: TrainingSessionRow): TrainingSessionOffline {
    return {
      id: row.id,
      user_id: row.user_id,
      title: row.title,
      date: row.date,
      duration: row.duration,
      location: row.location,
      session_type: row.session_type,
      techniques_practiced: row.techniques_practiced || [],
      notes: row.notes || '',
      belt_rank: row.belt_rank || '',
      stripe_count: row.stripe_count || 0,
      professor: row.professor,
      learned: row.learned,
      improvements: row.improvements,
      successes: row.successes,
      youtube_url: row.youtube_url,
      competition_finish: row.competition_finish,
      competition_matches: Array.isArray(row.competition_matches)
        ? row.competition_matches as Array<{
            opponent?: string;
            result?: string;
            submission?: string;
            notes?: string;
          }>
        : [],
      created_at: row.created_at || new Date().toISOString(),
      updated_at: row.updated_at || new Date().toISOString(),
      _sync_status: 'synced'
    };
  }

  /**
   * Convert offline format to Supabase insert
   */
  static toSupabaseInsert(offline: TrainingSessionOffline): TrainingSessionInsert {
    return {
      id: offline.id,
      user_id: offline.user_id,
      title: offline.title,
      date: offline.date,
      duration: offline.duration,
      location: offline.location,
      session_type: offline.session_type,
      techniques_practiced: offline.techniques_practiced,
      notes: offline.notes,
      belt_rank: offline.belt_rank,
      stripe_count: offline.stripe_count,
      professor: offline.professor,
      learned: offline.learned,
      improvements: offline.improvements,
      successes: offline.successes,
      youtube_url: offline.youtube_url,
      competition_finish: offline.competition_finish,
      competition_matches: offline.competition_matches,
      created_at: offline.created_at,
      updated_at: offline.updated_at
    };
  }

  /**
   * Convert offline format to Supabase update
   */
  static toSupabaseUpdate(offline: TrainingSessionOffline): Partial<TrainingSessionRow> {
    return {
      title: offline.title,
      date: offline.date,
      duration: offline.duration,
      location: offline.location,
      session_type: offline.session_type,
      techniques_practiced: offline.techniques_practiced,
      notes: offline.notes,
      belt_rank: offline.belt_rank,
      stripe_count: offline.stripe_count,
      professor: offline.professor,
      learned: offline.learned,
      improvements: offline.improvements,
      successes: offline.successes,
      youtube_url: offline.youtube_url,
      competition_finish: offline.competition_finish,
      competition_matches: offline.competition_matches,
      updated_at: new Date().toISOString()
    };
  }
}

/**
 * Technique Transformations
 */
export class TechniqueTransformer {
  /**
   * Convert Supabase row to offline format
   */
  static fromSupabase(row: TechniqueRow): TechniqueOffline {
    return {
      id: row.id,
      name: row.name,
      slug: row.slug,
      description: row.description || '',
      position_id: row.position_id || '',
      technique_position: (row.technique_position || '') as TechniqueOffline['technique_position'],
      technique_type: (row.technique_type || '') as TechniqueOffline['technique_type'],
      difficulty: (row.difficulty || '') as TechniqueOffline['difficulty'],
      image_url: row.image_url || undefined,
      video_url: row.video_url || undefined,
      key_points: Array.isArray(row.key_points) ? row.key_points : [],
      common_mistakes: Array.isArray(row.common_mistakes) ? row.common_mistakes : [],
      category: row.category || undefined,
      subcategory: row.subcategory || undefined,
      is_admin_created: (row.is_admin_created || false) ? 1 : 0, // Convert to number for IndexedDB
      is_user_created: (row.is_user_created || false) ? 1 : 0, // Convert to number for IndexedDB
      created_by_user_id: row.created_by_user_id || undefined,
      is_private: (row.is_private || false) ? 1 : 0, // Convert to number for IndexedDB
      created_at: row.created_at || new Date().toISOString(),
      updated_at: row.updated_at || new Date().toISOString(),
      _sync_status: 'synced'
    };
  }

  /**
   * Convert offline format to Supabase insert
   */
  static toSupabaseInsert(offline: TechniqueOffline): TechniqueInsert {
    return {
      id: offline.id,
      name: offline.name,
      slug: offline.slug,
      description: offline.description,
      position_id: offline.position_id,
      technique_position: offline.technique_position,
      technique_type: offline.technique_type,
      difficulty: offline.difficulty,
      image_url: offline.image_url,
      video_url: offline.video_url,
      key_points: offline.key_points,
      common_mistakes: offline.common_mistakes,
      category: offline.category,
      subcategory: offline.subcategory,
      is_admin_created: Boolean(offline.is_admin_created), // Convert from number to boolean
      is_user_created: Boolean(offline.is_user_created), // Convert from number to boolean
      created_by_user_id: offline.created_by_user_id,
      is_private: Boolean(offline.is_private), // Convert from number to boolean
      created_at: offline.created_at,
      updated_at: offline.updated_at
    };
  }
}

/**
 * User Technique Transformations
 */
export class UserTechniqueTransformer {
  /**
   * Convert Supabase row to offline format
   */
  static fromSupabase(row: UserTechniqueRow): UserTechniqueOffline {
    return {
      id: row.id,
      user_id: row.user_id,
      technique_id: row.technique_id,
      notes: row.notes || undefined,
      proficiency_level: row.proficiency_level || 'learning', // Keep as string to match database
      last_practiced: row.last_practiced || undefined,
      times_practiced: row.times_practiced || 0,
      is_favorite: (row.is_favorite || false) ? 1 : 0, // Convert to number for IndexedDB
      created_at: row.created_at || new Date().toISOString(),
      updated_at: row.updated_at || new Date().toISOString(),
      _sync_status: 'synced'
    };
  }

  /**
   * Convert offline format to Supabase insert
   */
  static toSupabaseInsert(offline: UserTechniqueOffline): UserTechniqueInsert {
    return {
      id: offline.id,
      user_id: offline.user_id,
      technique_id: offline.technique_id,
      notes: offline.notes,
      proficiency_level: offline.proficiency_level, // Already a string, no conversion needed
      last_practiced: offline.last_practiced,
      times_practiced: offline.times_practiced,
      is_favorite: Boolean(offline.is_favorite), // Convert from number to boolean
      created_at: offline.created_at,
      updated_at: offline.updated_at
    };
  }
}

/**
 * Position Transformations
 */
export class PositionTransformer {
  /**
   * Convert Supabase row to offline format
   */
  static fromSupabase(row: PositionRow): PositionOffline {
    return {
      id: row.id,
      name: row.name,
      description: row.description || undefined,
      category: row.category || undefined,
      parent_id: (row as PositionRow & { parent_id?: string }).parent_id || undefined,
      created_at: row.created_at || new Date().toISOString(),
      updated_at: (row as PositionRow & { updated_at?: string }).updated_at || row.created_at || new Date().toISOString()
    };
  }

  /**
   * Convert offline format to Supabase insert format
   * Handles both parent_id (correct) and parentId (legacy) formats
   */
  static toSupabaseInsert(data: PositionOffline | Record<string, unknown>): Omit<PositionRow, 'id' | 'created_at'> {
    // Handle legacy data that might have parentId instead of parent_id
    const parentId = (data as Record<string, unknown>).parent_id || (data as Record<string, unknown>).parentId;

    return {
      name: (data as Record<string, unknown>).name as string,
      description: (data as Record<string, unknown>).description as string || null,
      category: (data as Record<string, unknown>).category as string || null,
      parent_id: parentId ? (parentId as string) : null
    };
  }

  /**
   * Convert offline format to Supabase update format
   * Handles both parent_id (correct) and parentId (legacy) formats
   */
  static toSupabaseUpdate(data: PositionOffline | Record<string, unknown>): Partial<PositionRow> {
    const updateData: Partial<PositionRow> = {};

    if ((data as Record<string, unknown>).name !== undefined) updateData.name = (data as Record<string, unknown>).name as string;
    if ((data as Record<string, unknown>).description !== undefined) updateData.description = (data as Record<string, unknown>).description as string || null;
    if ((data as Record<string, unknown>).category !== undefined) updateData.category = (data as Record<string, unknown>).category as string || null;

    // Handle both parent_id and parentId for backward compatibility
    const parentId = (data as Record<string, unknown>).parent_id || (data as Record<string, unknown>).parentId;
    if (parentId !== undefined) updateData.parent_id = parentId ? (parentId as string) : null;

    return updateData;
  }
}

/**
 * Game Plan Transformations
 */
export class GamePlanTransformer {
  /**
   * Convert Supabase row to offline format
   */
  static fromSupabase(row: GamePlanRow): GamePlanOffline {
    return {
      id: row.id,
      name: row.name,
      description: row.description || undefined,
      thumbnail: (row as GamePlanRow & { thumbnail?: string }).thumbnail || undefined, // Available in database but not in types
      flow_data: typeof row.flow_data === 'object' && row.flow_data !== null
        ? row.flow_data as {
            nodes: Array<{
              id: string;
              type?: string;
              position?: { x: number; y: number };
              data?: Record<string, unknown>;
            }>;
            edges: Array<{
              id: string;
              source: string;
              target: string;
              type?: string;
              data?: Record<string, unknown>;
            }>;
          }
        : { nodes: [], edges: [] },
      user_id: row.user_id,
      is_public: ((row as GamePlanRow & { is_public?: boolean }).is_public || false) ? 1 : 0, // Convert to number for IndexedDB
      is_template: (row.is_template || false) ? 1 : 0, // Convert to number for IndexedDB
      is_admin_template: (row.is_admin_template || false) ? 1 : 0, // Convert to number for IndexedDB
      created_at: row.created_at || new Date().toISOString(),
      updated_at: row.updated_at || new Date().toISOString(),
      _sync_status: 'synced'
    };
  }

  /**
   * Convert offline format to Supabase insert
   */
  static toSupabaseInsert(offline: GamePlanOffline): GamePlanInsert {
    return {
      id: offline.id,
      name: offline.name,
      description: offline.description,
      thumbnail: offline.thumbnail,
      flow_data: offline.flow_data as unknown as Json,
      user_id: offline.user_id,
      is_public: Boolean(offline.is_public), // Convert from number to boolean
      is_template: Boolean(offline.is_template), // Convert from number to boolean
      is_admin_template: Boolean(offline.is_admin_template), // Convert from number to boolean
      created_at: offline.created_at,
      updated_at: offline.updated_at
    } as GamePlanInsert & { thumbnail?: string; is_public?: boolean }; // Type assertion needed due to outdated types
  }
}

/**
 * User Profile Transformations
 */
export class UserProfileTransformer {
  /**
   * Convert Supabase row to offline format
   */
  static fromSupabase(row: ProfileRow): UserProfileOffline {
    return {
      id: row.id,
      username: row.username || undefined,
      full_name: row.full_name || undefined,
      avatar_url: row.avatar_url || undefined,
      home_gym: row.home_gym || undefined,
      belt_rank: row.belt_rank || undefined,
      stripes: row.stripes?.toString() || undefined,
      email_preferences: typeof row.email_preferences === 'object' && row.email_preferences !== null
        ? row.email_preferences as {
            weekly_summary: boolean;
            weekly_insights: boolean;
            last_updated: string;
          }
        : {
            weekly_summary: true,
            weekly_insights: true,
            last_updated: new Date().toISOString()
          },
      created_at: row.created_at || new Date().toISOString(),
      updated_at: row.updated_at || new Date().toISOString(),
      _sync_status: 'synced'
    };
  }

  /**
   * Convert offline format to Supabase update
   */
  static toSupabaseUpdate(offline: UserProfileOffline): ProfileUpdate {
    const update: ProfileUpdate = {
      username: offline.username,
      full_name: offline.full_name,
      home_gym: offline.home_gym,
      belt_rank: offline.belt_rank,
      stripes: offline.stripes ? parseInt(offline.stripes, 10) : null,
      email_preferences: offline.email_preferences,
      updated_at: new Date().toISOString()
    };

    // Only include avatar_url if it's explicitly defined to avoid clearing existing values
    if (offline.avatar_url !== undefined) {
      update.avatar_url = offline.avatar_url;
    }

    return update;
  }
}

/**
 * Utility functions for data transformation
 */
export class DataTransformUtils {
  /**
   * Remove offline-specific metadata from a record
   */
  static stripOfflineMetadata<T extends Record<string, unknown>>(record: T): Omit<T, '_offline_created' | '_offline_updated' | '_sync_status' | '_last_modified'> {
    const { _offline_created, _offline_updated, _sync_status, _last_modified, ...cleanRecord } = record;
    return cleanRecord;
  }

  /**
   * Add offline metadata to a record
   */
  static addOfflineMetadata<T extends Record<string, unknown>>(
    record: T, 
    status: 'pending' | 'synced' | 'conflict' = 'pending',
    isNew = false
  ): T & { _sync_status: string; _last_modified: string } {
    return {
      ...record,
      _sync_status: status,
      _last_modified: new Date().toISOString(),
      ...(isNew && { _offline_created: true }),
      ...(!isNew && { _offline_updated: true })
    };
  }

  /**
   * Check if a record has offline changes
   */
  static hasOfflineChanges(record: Record<string, unknown>): boolean {
    return record._offline_created === true || 
           record._offline_updated === true || 
           record._sync_status === 'pending';
  }

  /**
   * Get the last modified timestamp from a record
   */
  static getLastModified(record: Record<string, unknown>): string {
    return (record._last_modified as string) || (record.updated_at as string) || (record.created_at as string) || new Date().toISOString();
  }
}

/**
 * Roadmap Macro Transformations
 */
export class RoadmapMacroTransformer {
  /**
   * Convert Supabase row to offline format
   */
  static fromSupabase(row: RoadmapMacroRow): RoadmapMacroOffline {
    return {
      id: row.id,
      title: row.title,
      belt_color: row.belt_color,
      stripe_count: row.stripe_count,
      content: row.content || undefined,
      image_url: row.image_url || undefined,
      video_url: row.video_url || undefined,
      order_index: row.order_index,
      is_active: (row.is_active || false) ? 1 : 0, // Convert to number for IndexedDB
      created_at: row.created_at || new Date().toISOString(),
      updated_at: row.updated_at || new Date().toISOString(),
      created_by: row.created_by || undefined,
      _sync_status: 'synced'
    };
  }
}

/**
 * Roadmap Meso Transformations
 */
export class RoadmapMesoTransformer {
  /**
   * Convert Supabase row to offline format
   */
  static fromSupabase(row: RoadmapMesoRow): RoadmapMesoOffline {
    return {
      id: row.id,
      title: row.title,
      belt_color: row.belt_color,
      stripe_count: row.stripe_count,
      content: row.content || undefined,
      image_url: row.image_url || undefined,
      video_url: row.video_url || undefined,
      key_techniques: Array.isArray(row.key_techniques) ? row.key_techniques : [],
      learning_objectives: Array.isArray(row.learning_objectives) ? row.learning_objectives : [],
      order_index: row.order_index,
      is_active: (row.is_active || false) ? 1 : 0, // Convert to number for IndexedDB
      created_at: row.created_at || new Date().toISOString(),
      updated_at: row.updated_at || new Date().toISOString(),
      created_by: row.created_by || undefined,
      _sync_status: 'synced'
    };
  }
}
